import { useState, useCallback } from 'react';
import { kbArticleService } from '@/services/api';
import type { UpdateArticleOptions } from '@/services/api/kb-article-service';
import type { KBArticle, KBArticleCreatePayload, KBArticleUpdatePayload } from '@/common-types';
import type { UseKBArticlesReturn } from '@/types/application/common/hooks/use-kb-articles';

export function useKBArticles(): UseKBArticlesReturn {
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await kbArticleService.listArticles();
      setArticles(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  const createArticle = useCallback(async (payload: KBArticleCreatePayload, file: File) => {
    await kbArticleService.createArticle(payload, file);
    await refresh();
  }, [refresh]);

  const updateArticle = useCallback(async (
    articleId: string,
    payload: KBArticleUpdatePayload,
    options?: UpdateArticleOptions,
  ) => {
    await kbArticleService.updateArticle(articleId, payload, options);
    await refresh();
  }, [refresh]);

  const deleteArticle = useCallback(async (articleId: string) => {
    await kbArticleService.deleteArticle(articleId);
    await refresh();
  }, [refresh]);

  return { articles, loading, error, refresh, createArticle, updateArticle, deleteArticle };
}

export default useKBArticles;
